import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import * as L from 'leaflet';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzModalModule } from 'ng-zorro-antd/modal';
import {
  NzNotificationModule,
  NzNotificationService,
} from 'ng-zorro-antd/notification';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { AvatarComponent } from '@/app/common/components/avatar/avatar.component';
import { LoaderService } from '@/app/common/services/loader/loader.service';
import { VehicleRevisionService } from '@/app/modules/vehicle-revision/core/service';
import { VehicleRevisionDetail } from '@/app/modules/vehicle-revision/core/type';
import { VehicleTrackerService } from '@/app/modules/vehicle-tracker/core/service';
import {
  VehicleTrackerPosition,
  VehicleTrackerStatistics,
  VehicleTrackerTrailPoint,
} from '@/app/modules/vehicle-tracker/core/type';
import { normalizeTrackerChassis } from '@/app/modules/vehicle-tracker/core/utils';
import { VehicleFineService } from '@/app/modules/vehicle-fine/core/service';
import {
  FineStatusEnum,
  VehicleFine,
  VehicleFineSummary,
} from '@/app/modules/vehicle-fine/core/type';
import { environment } from '@/environments/environment';

type PeriodPreset = '30d' | 'today' | '7d' | 'custom';

const DEFAULT_CENTER: L.LatLngExpression = [-15.601, -56.097];
const DEFAULT_ZOOM = 5;
const MOVEMENTS_PAGE_SIZE = 20;

@Component({
  selector: 'app-vehicle-tracker-detail',
  standalone: true,
  templateUrl: './detail.component.html',
  styleUrls: ['./detail.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    NzAlertModule,
    NzButtonModule,
    NzDatePickerModule,
    NzDividerModule,
    NzEmptyModule,
    NzIconModule,
    NzInputModule,
    NzInputNumberModule,
    NzLayoutModule,
    NzModalModule,
    NzNotificationModule,
    NzPopconfirmModule,
    NzSelectModule,
    NzSpinModule,
    NzTableModule,
    NzTabsModule,
    NzTagModule,
    AvatarComponent,
  ],
})
export class VehicleTrackerDetailComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('trailMapContainer', { static: true })
  trailMapContainer!: ElementRef<HTMLDivElement>;

  chassis = '';
  loading = false;
  movementsLoading = false;
  errorMessage: string | null = null;
  statistics: VehicleTrackerStatistics | null = null;
  revision: VehicleRevisionDetail | null = null;
  movements: VehicleTrackerPosition[] = [];
  movementsTotal = 0;
  movementsPageIndex = 1;
  readonly movementsPageSize = MOVEMENTS_PAGE_SIZE;

  periodPreset: PeriodPreset = '30d';
  dateRange: Date[] = [];
  private fromIso = '';
  private toIso = '';

  fines: VehicleFine[] = [];
  finesTotal = 0;
  finesPageIndex = 1;
  readonly finesPageSize = 10;
  finesLoading = false;
  fineSummary: VehicleFineSummary | null = null;

  FineStatusEnum = FineStatusEnum;

  fineModalVisible = false;
  fineModalMode: 'create' | 'edit' = 'create';
  fineSaving = false;
  fineAmountDisplay = '';
  private editingFineId: number | null = null;
  fineForm: {
    amount: number | null;
    fine_date: Date | null;
    status: FineStatusEnum;
    description: string;
  } = this.emptyFineForm();

  private map?: L.Map;
  private trailPolyline?: L.Polyline;
  private trailMarker?: L.Marker | L.CircleMarker;
  private resizeObserver?: ResizeObserver;
  private pendingTrail: VehicleTrackerTrailPoint[] | null = null;
  private pendingLastPosition: VehicleTrackerPosition | null = null;
  private pendingFitBounds: L.LatLngBounds | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private vehicleTrackerService: VehicleTrackerService,
    private vehicleRevisionService: VehicleRevisionService,
    private vehicleFineService: VehicleFineService,
    private loaderService: LoaderService,
    private notification: NzNotificationService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const chassis = normalizeTrackerChassis(params.get('chassis') || '');
      if (!chassis) {
        this.errorMessage = 'Placa inválida.';
        return;
      }
      this.chassis = chassis;
      this.applyDefaultPeriod();
      this.loadAll(true);
    });
  }

  ngAfterViewInit(): void {
    this.ensureMap();
    this.flushPendingTrail();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroyMap();
  }

  goBack(): void {
    this.router.navigate(['/tracker']);
  }

  setPeriodPreset(preset: PeriodPreset): void {
    if (preset === 'custom') {
      this.periodPreset = 'custom';
      return;
    }
    this.periodPreset = preset;
    this.applyPeriodPreset(preset);
    this.movementsPageIndex = 1;
    this.finesPageIndex = 1;
    this.loadAll(true);
  }

  onCustomRangeChange(range: Date[] | null): void {
    if (!range || range.length !== 2 || !range[0] || !range[1]) {
      return;
    }
    this.periodPreset = 'custom';
    this.dateRange = range;
    this.fromIso = range[0].toISOString();
    this.toIso = range[1].toISOString();
    this.movementsPageIndex = 1;
    this.finesPageIndex = 1;
    this.loadAll(true);
  }

  onMovementsPageChange(pageIndex: number): void {
    this.movementsPageIndex = pageIndex;
    this.loadMovements(false);
  }

  onFinesPageChange(pageIndex: number): void {
    this.finesPageIndex = pageIndex;
    this.loadFines(false);
  }

  private loadFines(showLoader: boolean): void {
    if (!this.chassis) {
      return;
    }

    this.finesLoading = true;
    const request$ = this.vehicleFineService.findAll({
      chassis: this.chassis,
      pageIndex: this.finesPageIndex,
      pageSize: this.finesPageSize,
    });

    const source$ = showLoader
      ? this.loaderService.executeWithLoader(request$)
      : request$;

    source$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.fines = [...response.items];
        this.finesTotal = response.total;
        this.finesLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.finesLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  private loadFineSummary(): void {
    if (!this.chassis || !this.fromIso || !this.toIso) {
      return;
    }

    this.vehicleFineService
      .getSummary({ chassis: this.chassis, from: this.fromIso, to: this.toIso })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (summary) => {
          this.fineSummary = summary;
          this.cdr.detectChanges();
        },
      });
  }

  private refreshFines(resetPage = false): void {
    if (resetPage) {
      this.finesPageIndex = 1;
    }
    this.loadFines(false);
    this.loadFineSummary();
  }

  openCreateFine(): void {
    this.fineModalMode = 'create';
    this.editingFineId = null;
    this.fineForm = this.emptyFineForm();
    this.setFineAmountDisplay(this.fineForm.amount);
    this.fineModalVisible = true;
  }

  openEditFine(fine: VehicleFine): void {
    this.fineModalMode = 'edit';
    this.editingFineId = fine.id;
    this.fineForm = {
      amount: fine.amount,
      fine_date: fine.fine_date ? new Date(fine.fine_date) : null,
      status: fine.status,
      description: fine.description ?? '',
    };
    this.setFineAmountDisplay(this.fineForm.amount);
    this.fineModalVisible = true;
  }

  onFineAmountInput(value: string): void {
    const digits = (value || '').replace(/\D/g, '');
    if (!digits) {
      this.fineForm.amount = null;
      this.fineAmountDisplay = '';
      return;
    }
    const amount = Number(digits) / 100;
    this.fineForm.amount = amount;
    this.fineAmountDisplay = this.formatCurrency(amount);
  }

  private setFineAmountDisplay(amount: number | null): void {
    this.fineAmountDisplay = amount != null ? this.formatCurrency(amount) : '';
  }

  closeFineModal(): void {
    this.fineModalVisible = false;
    this.editingFineId = null;
  }

  submitFine(): void {
    if (this.fineForm.amount == null || this.fineForm.amount < 0) {
      this.notification.warning('Multa', 'Informe um valor válido.');
      return;
    }
    if (!this.fineForm.fine_date) {
      this.notification.warning('Multa', 'Informe a data da multa.');
      return;
    }

    this.fineSaving = true;

    const payload = {
      amount: this.fineForm.amount,
      fine_date: this.fineForm.fine_date.toISOString(),
      status: this.fineForm.status,
      description: this.fineForm.description?.trim() || undefined,
    };

    const request$ =
      this.fineModalMode === 'edit' && this.editingFineId != null
        ? this.vehicleFineService.update(this.editingFineId, payload)
        : this.vehicleFineService.create({ ...payload, chassis: this.chassis });

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.fineSaving = false;
        this.fineModalVisible = false;
        this.editingFineId = null;
        const wasCreate = this.fineModalMode === 'create';
        this.notification.success(
          'Multa',
          wasCreate
            ? 'Multa lançada com sucesso.'
            : 'Multa atualizada com sucesso.',
        );
        this.refreshFines(wasCreate);
      },
      error: (error) => {
        this.fineSaving = false;
        this.notification.error(
          `${error.status || ''}`,
          error?.error?.message || 'Erro ao salvar a multa.',
        );
      },
    });
  }

  toggleFineStatus(fine: VehicleFine): void {
    const next =
      fine.status === FineStatusEnum.PAID
        ? FineStatusEnum.UNPAID
        : FineStatusEnum.PAID;

    this.vehicleFineService
      .updateStatus(fine.id, next)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.refreshFines(),
        error: (error) => {
          this.notification.error(
            `${error.status || ''}`,
            error?.error?.message || 'Erro ao atualizar o status.',
          );
        },
      });
  }

  deleteFine(fine: VehicleFine): void {
    this.vehicleFineService
      .remove(fine.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notification.success('Multa', 'Multa removida.');
          this.refreshFines();
        },
        error: (error) => {
          this.notification.error(
            `${error.status || ''}`,
            error?.error?.message || 'Erro ao remover a multa.',
          );
        },
      });
  }

  fineStatusLabel(status: FineStatusEnum): string {
    return status === FineStatusEnum.PAID ? 'Paga' : 'Em aberto';
  }

  fineStatusColor(status: FineStatusEnum): string {
    return status === FineStatusEnum.PAID ? 'success' : 'warning';
  }

  formatCurrency(value: number | null | undefined): string {
    const amount = Number(value) || 0;
    return amount.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  }

  private emptyFineForm(): {
    amount: number | null;
    fine_date: Date | null;
    status: FineStatusEnum;
    description: string;
  } {
    return {
      amount: null,
      fine_date: new Date(),
      status: FineStatusEnum.UNPAID,
      description: '',
    };
  }

  formatDuration(totalSec: number | null | undefined): string {
    if (totalSec == null || !Number.isFinite(totalSec) || totalSec <= 0) {
      return '—';
    }
    const hours = Math.floor(totalSec / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remHours = hours % 24;
      return `${days}d ${remHours}h`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    }
    return `${minutes} min`;
  }

  formatIgnition(ignition?: string | null): string {
    if (ignition === 'L') {
      return 'Ligada';
    }
    if (ignition === 'D') {
      return 'Desligada';
    }
    return '—';
  }

  get motionLabel(): string {
    const speed = this.statistics?.lastPosition?.speed ?? 0;
    return speed > 0 ? 'Em movimento' : 'Parado';
  }

  get motionMoving(): boolean {
    return (this.statistics?.lastPosition?.speed ?? 0) > 0;
  }

  get trailPointCount(): number {
    return this.statistics?.trailSample?.length ?? 0;
  }

  private applyDefaultPeriod(): void {
    this.periodPreset = '30d';
    this.applyPeriodPreset('30d');
  }

  private applyPeriodPreset(preset: Exclude<PeriodPreset, 'custom'>): void {
    const now = new Date();
    const to = new Date(now);
    to.setHours(23, 59, 59, 999);
    let from: Date;

    if (preset === 'today') {
      from = new Date(now);
      from.setHours(0, 0, 0, 0);
    } else if (preset === '7d') {
      from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else {
      from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }

    this.dateRange = [from, to];
    this.fromIso = from.toISOString();
    this.toIso = to.toISOString();
  }

  private loadAll(showLoader: boolean): void {
    if (!this.chassis || !this.fromIso || !this.toIso) {
      return;
    }

    this.loading = true;
    this.errorMessage = null;

    const request$ = forkJoin({
      statistics: this.vehicleTrackerService.getStatisticsByChassis(
        this.chassis,
        { from: this.fromIso, to: this.toIso },
      ),
      revision: this.vehicleRevisionService
        .findByChassis(this.chassis)
        .pipe(catchError(() => of(null))),
      movements: this.vehicleTrackerService.findMovementsByChassis(
        this.chassis,
        {
          from: this.fromIso,
          to: this.toIso,
          pageIndex: this.movementsPageIndex,
          pageSize: this.movementsPageSize,
        },
      ),
      fineSummary: this.vehicleFineService
        .getSummary({
          chassis: this.chassis,
          from: this.fromIso,
          to: this.toIso,
        })
        .pipe(catchError(() => of(null))),
      fines: this.vehicleFineService
        .findAll({
          chassis: this.chassis,
          pageIndex: this.finesPageIndex,
          pageSize: this.finesPageSize,
        })
        .pipe(catchError(() => of({ items: [], total: 0 }))),
    });

    const source$ = showLoader
      ? this.loaderService.executeWithLoader(request$)
      : request$;

    source$.pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ statistics, revision, movements, fineSummary, fines }) => {
        this.statistics = statistics;
        this.revision = revision;
        this.movements = movements.items;
        this.movementsTotal = movements.total;
        this.fineSummary = fineSummary;
        this.fines = fines.items;
        this.finesTotal = fines.total;
        this.loading = false;
        this.queueTrailRender(
          statistics.trailSample ?? [],
          statistics.lastPosition ?? null,
        );
      },
      error: (error) => {
        this.loading = false;
        this.statistics = null;
        this.movements = [];
        this.movementsTotal = 0;
        this.fines = [];
        this.finesTotal = 0;
        this.fineSummary = null;
        this.errorMessage =
          error?.error?.message ||
          'Não foi possível carregar o detalhe do rastreador.';
        this.destroyMap();
      },
    });
  }

  private loadMovements(showLoader: boolean): void {
    if (!this.chassis) {
      return;
    }

    this.movementsLoading = true;
    const request$ = this.vehicleTrackerService.findMovementsByChassis(
      this.chassis,
      {
        from: this.fromIso,
        to: this.toIso,
        pageIndex: this.movementsPageIndex,
        pageSize: this.movementsPageSize,
      },
    );

    const source$ = showLoader
      ? this.loaderService.executeWithLoader(request$)
      : request$;

    source$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.movements = response.items;
        this.movementsTotal = response.total;
        this.movementsLoading = false;
      },
      error: () => {
        this.movementsLoading = false;
        this.movements = [];
        this.movementsTotal = 0;
      },
    });
  }

  private queueTrailRender(
    trail: VehicleTrackerTrailPoint[],
    lastPosition: VehicleTrackerPosition | null,
  ): void {
    this.pendingTrail = trail;
    this.pendingLastPosition = lastPosition;
    // Let Angular create/update #trailMapContainer before Leaflet mounts.
    setTimeout(() => this.flushPendingTrail(), 0);
  }

  private flushPendingTrail(): void {
    if (this.pendingTrail == null) {
      return;
    }
    if (!this.trailMapContainer?.nativeElement) {
      return;
    }

    const trail = this.pendingTrail;
    const lastPosition = this.pendingLastPosition;
    this.pendingTrail = null;
    this.pendingLastPosition = null;

    this.ensureMap();
    this.renderTrail(trail, lastPosition);
  }

  private ensureMap(): void {
    const el = this.trailMapContainer?.nativeElement;
    if (!el) {
      return;
    }

    if (this.map && this.map.getContainer() !== el) {
      this.destroyMap();
    }

    if (!this.map) {
      this.map = L.map(el, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        zoomControl: true,
      });

      const { urlTemplate, attribution, maxZoom } = environment.mapTiles;
      L.tileLayer(urlTemplate, { attribution, maxZoom }).addTo(this.map);

      this.resizeObserver = new ResizeObserver(() => {
        this.map?.invalidateSize({ animate: false });
      });
      this.resizeObserver.observe(el);
    }

    this.scheduleInvalidateSize();
  }

  private destroyMap(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.clearTrailLayers();
    this.map?.remove();
    this.map = undefined;
  }

  private clearTrailLayers(): void {
    this.trailPolyline?.remove();
    this.trailPolyline = undefined;
    this.trailMarker?.remove();
    this.trailMarker = undefined;
  }

  private renderTrail(
    trail: VehicleTrackerTrailPoint[],
    lastPosition: VehicleTrackerPosition | null,
  ): void {
    if (!this.map) {
      this.pendingTrail = trail;
      this.pendingLastPosition = lastPosition;
      return;
    }

    this.clearTrailLayers();

    const points = trail
      .map((p) => ({
        latitude: Number(p.latitude),
        longitude: Number(p.longitude),
        recordedAt: p.recordedAt,
      }))
      .filter(
        (p) =>
          Number.isFinite(p.latitude) &&
          Number.isFinite(p.longitude) &&
          !(p.latitude === 0 && p.longitude === 0),
      );

    if (points.length >= 2) {
      this.trailPolyline = L.polyline(
        points.map((p) => [p.latitude, p.longitude] as L.LatLngExpression),
        {
          color: '#c0142c',
          weight: 5,
          opacity: 0.95,
          lineJoin: 'round',
          lineCap: 'round',
        },
      ).addTo(this.map);
    } else if (points.length === 1) {
      this.trailMarker = L.circleMarker(
        [points[0].latitude, points[0].longitude],
        {
          radius: 6,
          color: '#c0142c',
          fillColor: '#c0142c',
          fillOpacity: 0.95,
        },
      ).addTo(this.map);
    }

    const lastLat = lastPosition ? Number(lastPosition.latitude) : NaN;
    const lastLng = lastPosition ? Number(lastPosition.longitude) : NaN;
    const markerPoint =
      Number.isFinite(lastLat) && Number.isFinite(lastLng)
        ? { latitude: lastLat, longitude: lastLng }
        : points.length
          ? points[points.length - 1]
          : null;

    if (markerPoint) {
      const moving =
        lastPosition != null ? (Number(lastPosition.speed) || 0) > 0 : false;
      this.trailMarker = L.marker(
        [markerPoint.latitude, markerPoint.longitude],
        {
          icon: this.createMarkerIcon(moving),
          zIndexOffset: 500,
        },
      ).addTo(this.map);
    }

    if (points.length >= 2 && this.trailPolyline) {
      this.pendingFitBounds = this.trailPolyline.getBounds();
    } else if (markerPoint) {
      this.pendingFitBounds = L.latLngBounds([
        [markerPoint.latitude, markerPoint.longitude],
        [markerPoint.latitude, markerPoint.longitude],
      ]);
    } else {
      this.pendingFitBounds = null;
      this.map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    }

    this.scheduleInvalidateSize();
  }

  private createMarkerIcon(moving: boolean): L.DivIcon {
    const stateClass = moving ? ' tracker-detail-marker--moving' : '';
    return L.divIcon({
      className: 'tracker-detail-marker',
      html: `<div class="tracker-detail-marker__pin${stateClass}"></div>`,
      iconSize: [18, 18],
      iconAnchor: [9, 9],
    });
  }

  private scheduleInvalidateSize(): void {
    const refresh = () => {
      if (!this.map) {
        return;
      }
      this.map.invalidateSize({ animate: false });
      if (this.pendingFitBounds?.isValid()) {
        const pointCount =
          this.trailPolyline?.getLatLngs()?.length ??
          (this.trailMarker ? 1 : 0);
        if (pointCount >= 2) {
          this.map.fitBounds(this.pendingFitBounds, {
            padding: [48, 48],
            maxZoom: 15,
          });
        } else {
          this.map.setView(this.pendingFitBounds.getCenter(), 14);
        }
      }
      this.trailPolyline?.bringToFront();
    };
    setTimeout(refresh, 0);
    setTimeout(refresh, 150);
    setTimeout(refresh, 400);
  }
}
